import axios from "axios";
import { DemoAirline } from "../config/movmoIdentity";

// Movmo API configuration — mirrors darwinApi.ts. Unlike the Darwin proxy
// routes, /v1/demo/airline/* is registered with ApiKeyRequired: false at the
// API Gateway level (applications-persistence-layer/api_gateway.yaml) and
// AuthorizationType: None — verified in monolith-api/routes/demo_airline.go
// (RegisterRoutes has no session/RBAC middleware) and the gateway template's
// v1DemoAirlineProxyResource*Method resources. No X-Api-Key header is sent.
const MOVMO_API_URL =
    process.env.REACT_APP_MOVMO_API_URL || "https://e2e.api.movmo.io";

function demoAirlineUrl(airline: DemoAirline, path: string): string {
    return `${MOVMO_API_URL}/v1/demo/airline/${airline}${path}`;
}

// --- POST /v1/demo/airline/{airline}/session ---
//
// SIMULATION STUB (mirrors monolith-api/docs/demo-airline.md's warning): a
// real airline partner does NOT build this — /session stands in for the
// airline's own pre-existing customer authentication, which this demo has no
// equivalent of. It exists only so this harness can obtain a DemoSession
// bearer token to authenticate POST /link/complete and GET /me (verified
// against monolith-api/routes/demo_airline.go: both call demoSessionAuth and
// 401 without one). See the AeroConnectCallback and PaConnectCallback
// components for how this is minted silently, with no login UI, from a
// per-browser synthetic demo email.

export interface DemoAirlineSessionResponse {
    traveler_id: string;
    display_name: string;
    session_token: string;
    expires_in: number;
}

export const createDemoAirlineSession = async (
    airline: DemoAirline,
    email: string,
): Promise<DemoAirlineSessionResponse> => {
    const response = await axios.post<DemoAirlineSessionResponse>(
        demoAirlineUrl(airline, "/session"),
        { email },
        { headers: { "Content-Type": "application/json" } },
    );
    return response.data;
};

// --- POST /v1/demo/airline/{airline}/link/complete ---
//
// DemoSession-authed (Authorization: DemoSession <token>). Request fields
// mirror monolith-api's demoAirlineLinkCompleteRequest exactly:
// {code, code_verifier, redirect_uri}. `redirect_uri` is required for the OAuth
// redirect flow and omitted for recognition/FedCM codes (monolith #335).

export interface DemoAirlineLinkCompletePayload {
    code: string;
    code_verifier: string;
    redirect_uri?: string;
}

export interface DemoAirlineLinkCompleteResponse {
    linked: boolean;
}

export const linkComplete = async (
    airline: DemoAirline,
    sessionToken: string,
    payload: DemoAirlineLinkCompletePayload,
): Promise<DemoAirlineLinkCompleteResponse> => {
    const response = await axios.post<DemoAirlineLinkCompleteResponse>(
        demoAirlineUrl(airline, "/link/complete"),
        payload,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `DemoSession ${sessionToken}`,
            },
        },
    );
    return response.data;
};

// --- Traveler claim shapes (returned by GET /me and the loyalty-link calls) ---
//
// The airline session carries the traveler's Movmo identity once the chip's
// OAuth round trip has linked it (Flow B): GET /me resolves the stored GUID
// server-side. There is no anonymous lookup call.

export interface DemoAirlineProfileClaims {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    suffix?: string;
    email: string;
    phone_number?: string;
}

export interface DemoAirlineTravelPreferenceClaims {
    seat_type?: string;
    seating_zone?: string;
    extra_legroom: boolean;
    exit_row: boolean;
    check_bag: boolean;
    meal_types?: string[];
    dietary_notes?: string;
    special_assistance?: string[];
}

export interface DemoAirlineLocalePreferenceClaims {
    language_code: string;
    region_code: string;
    currency_code: string;
}

export interface DemoAirlinePreferenceClaims {
    travel?: DemoAirlineTravelPreferenceClaims;
    locale?: DemoAirlineLocalePreferenceClaims;
}

// MOVMO-567: wire shape for the `loyalty` claim group, present only when the
// traveler granted `loyalty.read` to this airline in Movmo. Mirrors
// monolith-api's routes/identity_resolve.go loyaltyProgramClaim/loyaltyClaims
// DTOs field-for-field (MOVMO-561/563) — demo_airline.go's me
// handlers pass this group through from the loopback /v1/identity/resolve
// call unchanged. This is the WIRE-AUTHORITATIVE source for PA's Mabuhay
// Miles display (see hooks/useLoyaltySession.ts) — never fabricate this
// shape client-side from fixture data.
export interface DemoAirlineLoyaltyProgramClaims {
    program_code: string;
    airline_name: string;
    airline_iata_code?: string;
    member_number: string;
    tier_status?: string;
    points?: number;
    verified: boolean;
}

export interface DemoAirlineLoyaltyClaims {
    programs: DemoAirlineLoyaltyProgramClaims[];
}

export interface DemoAirlineMeResponse {
    traveler: { traveler_id: string; display_name: string };
    // Wire shape of demo_airline.go getMe: the group keys are pointers in a
    // gin.H without omitempty, so an absent group arrives as `null`.
    movmo: {
        recognized: boolean;
        profile?: DemoAirlineProfileClaims | null;
        preferences?: DemoAirlinePreferenceClaims | null;
        loyalty?: DemoAirlineLoyaltyClaims | null;
    };
}

// --- GET /v1/demo/airline/{airline}/me ---
//
// DemoSession-authed. `movmo.recognized` is false when the session row has no
// stored Movmo GUID (never linked, or the Movmo side revoked: the backend
// self-heals on 404 unknown_guid).

export const getMe = async (
    airline: DemoAirline,
    sessionToken: string,
): Promise<DemoAirlineMeResponse> => {
    const response = await axios.get<DemoAirlineMeResponse>(
        demoAirlineUrl(airline, "/me"),
        { headers: { Authorization: `DemoSession ${sessionToken}` } },
    );
    return response.data;
};

// --- Silent per-browser DemoSession mint ---
//
// The "toy airline login" (an email/password form standing in for the
// airline's own customer auth) is explicitly out of scope for this demo
// (Brian, MOVMO-508). But POST /link/complete hard-requires a DemoSession
// bearer token (see comment above), so something has to mint one. Rather than
// build a login UI, this generates a random, per-browser, throwaway demo
// email once, caches it in localStorage, and silently mints (or re-mints on
// 401) a session the first time a connect flow actually needs one — no form,
// no visible "login" step. This is a demo-only convenience, NOT a pattern a
// real partner integration should copy (see docs/demo-airline.md's
// SIMULATION STUB warning) and is called out as a deviation in the PR body.

const DEMO_EMAIL_STORAGE_KEY = "movmo_demo_airline_traveler_email";
const SESSION_STORAGE_PREFIX = "movmo_demo_airline_session_";

/** Read-only: the per-browser demo identity, or null if this browser never ran a demo. */
function getDemoEmail(): string | null {
    return window.localStorage.getItem(DEMO_EMAIL_STORAGE_KEY);
}

function getOrCreateDemoEmail(): string {
    let email = window.localStorage.getItem(DEMO_EMAIL_STORAGE_KEY);
    if (!email) {
        const id =
            typeof crypto !== "undefined" && "randomUUID" in crypto
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        email = `demo-traveler+${id}@mockairline.movmo.io`;
        window.localStorage.setItem(DEMO_EMAIL_STORAGE_KEY, email);
    }
    return email;
}

function sessionStorageKey(airline: DemoAirline): string {
    return `${SESSION_STORAGE_PREFIX}${airline}`;
}

/**
 * Returns a cached DemoSession token for `airline`, minting (and caching) a
 * new one via the synthetic per-browser demo email if none is cached yet.
 * Invisible to the user — no login form, no redirect.
 */
export const getOrCreateDemoSessionToken = async (
    airline: DemoAirline,
): Promise<string> => {
    const cached = window.localStorage.getItem(sessionStorageKey(airline));
    if (cached) return cached;

    const email = getOrCreateDemoEmail();
    const session = await createDemoAirlineSession(airline, email);
    window.localStorage.setItem(sessionStorageKey(airline), session.session_token);
    return session.session_token;
};

function clearDemoSessionToken(airline: DemoAirline): void {
    window.localStorage.removeItem(sessionStorageKey(airline));
}

/**
 * Reads GET /me for the airline session. The per-browser demo email stands in
 * for a persistent airline login (a real airline keeps its customer logged in
 * for weeks), so an expired or missing DemoSession (24h server-side) is
 * re-minted silently from it: the same email resolves the same stored Movmo
 * link, and a returning traveler is recognized with zero clicks.
 *
 * - No cached token and no demo email: this browser never ran a demo here,
 *   return null with no request.
 * - Cached token 401s: clear it, mint once, retry once; a second 401 clears
 *   and returns null.
 * Other errors propagate.
 */
export const getMeWithCachedSession = async (
    airline: DemoAirline,
): Promise<DemoAirlineMeResponse | null> => {
    const cached = window.localStorage.getItem(sessionStorageKey(airline));
    if (!cached && !getDemoEmail()) return null;
    const token = cached ?? (await getOrCreateDemoSessionToken(airline));
    try {
        return await getMe(airline, token);
    } catch (err) {
        if (!(axios.isAxiosError(err) && err.response?.status === 401)) throw err;
        clearDemoSessionToken(airline);
    }
    const fresh = await getOrCreateDemoSessionToken(airline);
    try {
        return await getMe(airline, fresh);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearDemoSessionToken(airline);
            return null;
        }
        throw err;
    }
};

/**
 * linkComplete with the stale-session retry the connect callbacks need:
 * DemoSession tokens expire after 24h server-side (monolith-api
 * routes/demo_airline.go, demoSessionTTL) but the cache above lives in
 * localStorage indefinitely, so any browser that ran a demo more than a day
 * ago holds a token that 401s with {"error":"invalid_session"}. On that 401
 * we drop the cache, mint a fresh session, and retry ONCE. The retry is safe
 * for the single-use OAuth code: the monolith 401s in demoSessionAuth before
 * the code exchange runs, so the code is untouched on the failed attempt.
 * getMeWithCachedSession above follows the same clear, mint, retry-once
 * pattern on landing.
 */
export const linkCompleteWithSession = async (
    airline: DemoAirline,
    payload: DemoAirlineLinkCompletePayload,
): Promise<DemoAirlineLinkCompleteResponse> => {
    const token = await getOrCreateDemoSessionToken(airline);
    try {
        return await linkComplete(airline, token, payload);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearDemoSessionToken(airline);
            const fresh = await getOrCreateDemoSessionToken(airline);
            return linkComplete(airline, fresh, payload);
        }
        throw err;
    }
};

// --- POST/DELETE /v1/demo/airline/{airline}/loyalty/link (MOVMO-542) ---
//
// DemoSession-authed, same as /link/complete. Request/response field names
// verified field-for-field against monolith-api routes/demo_airline.go
// (branch MOVMO-542/loyalty-link-api): demoAirlineLoyaltyLinkRequest and the
// shared respondLoyaltyLinkResult() response builder. The demo airline
// verifies loyalty membership through its OWN vendored loyalty adapter and
// only then asserts the verified link to Movmo — Movmo never verifies
// membership itself. On success, `movmo` is the SAME wire shape /me
// return (a fresh server-side resolve), so it is a legitimate wire source —
// see hooks/linkedTravelerStore.ts's applyWireTraveler.

export interface DemoAirlineLoyaltyLinkRequest {
    /** Mode 1: link an existing membership (paired with `code`). */
    member_number?: string;
    /** Mode 1: the verification code for `member_number`. */
    code?: string;
    /** Mode 2: complete the loyalty-in-consent "linked" decision. */
    from_consent?: boolean;
    /** Mode 3: one-click enrollment from the Movmo profile. */
    enroll?: boolean;
}

export interface DemoAirlineLoyaltyLinkTravelerDTO {
    traveler_id: string;
    display_name: string;
}

// Same movmo shape as /me — reuses those DTOs directly so
// there is zero drift risk between the call sites.
export interface DemoAirlineLoyaltyLinkMovmoView {
    recognized: boolean;
    profile?: DemoAirlineProfileClaims;
    preferences?: DemoAirlinePreferenceClaims;
    loyalty?: DemoAirlineLoyaltyClaims;
}

export interface DemoAirlineLoyaltyLinkResponse {
    linked: boolean;
    // Omitted by the backend when the post-link refresh 404s (consent
    // revoked mid-flight) — the link/unlink operation itself still
    // succeeded upstream by that point.
    traveler?: DemoAirlineLoyaltyLinkTravelerDTO;
    movmo: DemoAirlineLoyaltyLinkMovmoView;
}

// Machine-readable error codes the backend returns in {"error": ...} bodies
// for this pair of routes (400/401/404/409/422/502 — see routes/demo_airline.go).
export type DemoAirlineLoyaltyLinkErrorCode =
    | "invalid_request"
    | "invalid_session"
    | "not_connected"
    | "not_recognized"
    | "verification_failed"
    | "membership_not_found"
    | "no_loyalty_program"
    | "upstream_unavailable"
    | "upstream_error";

/** Extracts `{"error": "..."}` from an axios error response, if present. */
export function demoAirlineLoyaltyLinkErrorCode(
    err: unknown,
): DemoAirlineLoyaltyLinkErrorCode | undefined {
    if (!axios.isAxiosError(err)) return undefined;
    const data = err.response?.data as { error?: string } | undefined;
    return data?.error as DemoAirlineLoyaltyLinkErrorCode | undefined;
}

export const linkLoyalty = async (
    airline: DemoAirline,
    sessionToken: string,
    payload: DemoAirlineLoyaltyLinkRequest,
): Promise<DemoAirlineLoyaltyLinkResponse> => {
    const response = await axios.post<DemoAirlineLoyaltyLinkResponse>(
        demoAirlineUrl(airline, "/loyalty/link"),
        payload,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `DemoSession ${sessionToken}`,
            },
        },
    );
    return response.data;
};

export const unlinkLoyalty = async (
    airline: DemoAirline,
    sessionToken: string,
): Promise<DemoAirlineLoyaltyLinkResponse> => {
    const response = await axios.delete<DemoAirlineLoyaltyLinkResponse>(
        demoAirlineUrl(airline, "/loyalty/link"),
        { headers: { Authorization: `DemoSession ${sessionToken}` } },
    );
    return response.data;
};

/** linkLoyalty with the same stale-session 401 retry-once as linkCompleteWithSession. */
export const linkLoyaltyWithSession = async (
    airline: DemoAirline,
    payload: DemoAirlineLoyaltyLinkRequest,
): Promise<DemoAirlineLoyaltyLinkResponse> => {
    const token = await getOrCreateDemoSessionToken(airline);
    try {
        return await linkLoyalty(airline, token, payload);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearDemoSessionToken(airline);
            const fresh = await getOrCreateDemoSessionToken(airline);
            return linkLoyalty(airline, fresh, payload);
        }
        throw err;
    }
};

/** unlinkLoyalty with the same stale-session 401 retry-once as linkCompleteWithSession. */
export const unlinkLoyaltyWithSession = async (
    airline: DemoAirline,
): Promise<DemoAirlineLoyaltyLinkResponse> => {
    const token = await getOrCreateDemoSessionToken(airline);
    try {
        return await unlinkLoyalty(airline, token);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearDemoSessionToken(airline);
            const fresh = await getOrCreateDemoSessionToken(airline);
            return unlinkLoyalty(airline, fresh);
        }
        throw err;
    }
};

// --- DELETE /v1/demo/airline/{airline}/link ("Not you? Forget me") ---
//
// DemoSession-authed. Clears the session row's stored Movmo GUID so GET /me
// reads recognized:false again. Idempotent server-side.

export const unlink = async (
    airline: DemoAirline,
    sessionToken: string,
): Promise<void> => {
    await axios.delete(demoAirlineUrl(airline, "/link"), {
        headers: { Authorization: `DemoSession ${sessionToken}` },
    });
};

/**
 * unlink for a browser holding a DemoSession. No cached session means never
 * linked: nothing to do. A stale session (401) is already unlinked (the row is
 * gone), so the cache is dropped and the call counts as done.
 */
export const unlinkWithSession = async (
    airline: DemoAirline,
): Promise<void> => {
    const token = window.localStorage.getItem(sessionStorageKey(airline));
    if (!token) return;
    try {
        await unlink(airline, token);
    } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
            clearDemoSessionToken(airline);
            return;
        }
        throw err;
    }
};
